import {
	OfferStatus as SharedOfferStatus,
} from '@components/shared/FarmerOffersPage';
import { useAppbarTitle } from '@hooks/useAppbarTitle';
import useLanguageNavigate from '@hooks/useLanguageNavigate';
import { usePageTitle } from '@hooks/usePageTitle';
import { mdiArrowLeft } from '@mdi/js';
import Icon from '@mdi/react';
import {
	Box,
	Button,
	CircularProgress,
	Container,
	FormControl,
	Grid,
	InputLabel,
	MenuItem,
	Select,
	SelectChangeEvent,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { OffersList, RequestsList } from '../components';
import {
	getRequests as getRequestsService,
	getOffersForRequest,
	OfferStatus as ExpressOfferStatus,
	useFarmerExpressOffersStore,
} from '../services';
import { EmptyPage } from '@components/EmptyPage';

// Section loading component
const SectionLoader = () => {
	return (
		<Box
			sx={{
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
				height: '50vh',
				width: '100%',
			}}
		>
			<CircularProgress size={50} sx={{ color: '#3da08f' }} />
		</Box>
	);
};

export const ExpressOffersPage: React.FC = () => {
	const { t } = useTranslation();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const { navigateWithLanguage } = useLanguageNavigate();
	
	// Get state from store
	const {
		requests,
		offers,
		selectedRequestId,
		isRequestsLoading,
		isOffersLoading,
		requestsError,
		offersError,
		setRequests,
		setOffers,
		setSelectedRequestId,
		setIsRequestsLoading,
		setIsOffersLoading,
		setRequestsError,
		setOffersError
	} = useFarmerExpressOffersStore();
	
	const [statusFilter, setStatusFilter] = useState<SharedOfferStatus>('ongoing');
	
	// Track loading state locally to avoid issues with prop updates
	const [isContentLoading, setIsContentLoading] = useState(true);
	const apiCallInProgressRef = useRef(false);

	useAppbarTitle(t('express'));
	usePageTitle(t('express'));

	// Map shared statuses to Express statuses
	const getExpressStatus = (status: SharedOfferStatus): ExpressOfferStatus => {
		const statusMap: Record<SharedOfferStatus, ExpressOfferStatus> = {
			ongoing: 'active',
			completed: 'completed',
			archived: 'archived',
		};
		return statusMap[status];
	};

	// Handle status filter change
	const handleStatusFilterChange = (event: SelectChangeEvent) => {
		const newStatus = event.target.value as SharedOfferStatus;
		console.log(`[ExpressOffersPage] Status filter changed to ${newStatus}`);
		setStatusFilter(newStatus);
		setSelectedRequestId(null);
		setOffers([]);
		setIsContentLoading(true);
		
		// Fetch requests for the new status
		fetchRequests(newStatus);
	};
	
	// Fetch requests based on status
	const fetchRequests = async (status: SharedOfferStatus) => {
		try {
			console.log(`[ExpressOffersPage] Fetching ${status} requests...`);
			setIsRequestsLoading(true);
			setIsContentLoading(true);
			apiCallInProgressRef.current = true;
			
			const response = await getRequestsService(getExpressStatus(status));
			console.log(`[ExpressOffersPage] Received ${response.data.requests.length} requests`);
			
			const requestsData = response.data.requests;
			setRequests(requestsData);
			setRequestsError(null);
			
			// Auto-select first request if available
			if (requestsData.length > 0) {
				const firstRequest = requestsData[0];
				const requestId = firstRequest.request_id || firstRequest.id;
				setSelectedRequestId(requestId);
				
				// Fetch offers for the first request
				await fetchOffersForRequest(requestId, firstRequest);
			} else {
				// No requests available, no need to fetch offers
				setOffers([]);
				setIsOffersLoading(false);
				setIsContentLoading(false);
				apiCallInProgressRef.current = false;
			}
		} catch (error) {
			console.error('[ExpressOffersPage] Error fetching requests:', error);
			setRequestsError(error instanceof Error ? error.message : 'Error fetching requests');
			setRequests([]);
			setSelectedRequestId(null);
			setOffers([]);
			setIsContentLoading(false);
			apiCallInProgressRef.current = false;
		} finally {
			setIsRequestsLoading(false);
			
			// If no requests or error, we've already set loading to false
			// Otherwise, loading will be set to false after offers are loaded
		}
	};
	
	// Fetch offers for a specific request
	const fetchOffersForRequest = async (requestId: string, request: any) => {
		try {
			const serviceId = request.services_id || request.service_id || '';
			if (!serviceId) {
				console.log(`[ExpressOffersPage] No service ID found for request ${requestId}`);
				setOffers([]);
				setIsOffersLoading(false);
				setIsContentLoading(false);
				apiCallInProgressRef.current = false;
				return;
			}
			
			console.log(`[ExpressOffersPage] Fetching offers for request ${requestId}...`);
			setIsOffersLoading(true);
			setIsContentLoading(true);
			apiCallInProgressRef.current = true;
			
			const response = await getOffersForRequest(requestId, serviceId);
			console.log(`[ExpressOffersPage] Received ${response.data.offers.length} offers`);
			
			// Update the state with the response data
			setOffers(response.data.offers);
			setOffersError(null);
		} catch (error) {
			console.error('[ExpressOffersPage] Error fetching offers:', error);
			setOffersError(error instanceof Error ? error.message : 'Error fetching offers');
			setOffers([]);
		} finally {
			setIsOffersLoading(false);
			
			// Add a small delay to ensure smooth transition
			setTimeout(() => {
				setIsContentLoading(false);
				apiCallInProgressRef.current = false;
			}, 300);
		}
	};
	
	// Function to refresh offers for the current request
	const refreshOffers = useCallback(() => {
		if (selectedRequestId) {
			const selectedRequest = requests.find(req => (req.request_id || req.id) === selectedRequestId);
			if (selectedRequest) {
				console.log(`[ExpressOffersPage] Refreshing offers for request ${selectedRequestId} with a delay to prevent race conditions`);
				
				// Add a slight delay before refreshing to prevent race conditions
				setTimeout(() => {
					fetchOffersForRequest(selectedRequestId, selectedRequest);
				}, 500); // 500ms delay
			}
		}
	}, [selectedRequestId, requests]);
	
	// Function to refresh requests for the current status
	const refreshRequests = useCallback(() => {
		console.log(`[ExpressOffersPage] Refreshing requests for status ${statusFilter}`);
		
		// Add a slight delay before refreshing to prevent race conditions
		setTimeout(() => {
			fetchRequests(statusFilter);
		}, 500); // 500ms delay
	}, [statusFilter]);
	
	// Function to refresh both requests and offers
	const refreshAll = useCallback(() => {
		console.log('[ExpressOffersPage] Refreshing both requests and offers');
		refreshRequests();
	}, [refreshRequests]);
	
	// Handle selection of a request
	const handleSelectRequest = useCallback((requestId: string) => {
		// For desktop view, update the selected request ID and fetch offers
		if (!isMobile) {
			console.log(`[ExpressOffersPage] Request selected: ${requestId}`);
			setSelectedRequestId(requestId);
			setIsContentLoading(true);
			const selectedRequest = requests.find(req => (req.request_id || req.id) === requestId);
			if (selectedRequest) {
				fetchOffersForRequest(requestId, selectedRequest);
			} else {
				console.error(`[ExpressOffersPage] Selected request not found: ${requestId}`);
				setIsOffersLoading(false);
				setIsContentLoading(false);
				apiCallInProgressRef.current = false;
			}
		}
	}, [isMobile, requests]);

	// Initial data fetch
	useEffect(() => {
		console.log("[ExpressOffersPage] Initial component mount, starting data fetch");
		
		// Clear any previous loading state
		localStorage.removeItem('express_loading');
		
		// Set initial loading state
		setIsContentLoading(true);
		
		// Fetch initial data
		fetchRequests('ongoing');
		
		return () => {
			console.log("[ExpressOffersPage] Component unmounting, cleaning up");
			localStorage.removeItem('express_loading');
		};
	}, []);

	// Effect to handle loading state based on API loading states
	useEffect(() => {
		// If API is loading, ensure content loading is shown
		if (isRequestsLoading || isOffersLoading) {
			if (!isContentLoading) {
				console.log('[ExpressOffersPage] API loading detected, showing loader');
				setIsContentLoading(true);
			}
		}
	}, [isRequestsLoading, isOffersLoading, isContentLoading]);

	// Handle going back to view-offers
	const handleGoBack = () => {
		navigateWithLanguage('/farmer/view-offers');
	};

	const isEmpty = requests.length === 0;
	
	console.log(`[ExpressOffersPage] Rendering content: isEmpty=${isEmpty}, requests=${requests.length}, offers=${offers.length}, isLoading=${isContentLoading}`);

	// Header section is always visible
	const headerSection = (
		<Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
			<Button
				onClick={handleGoBack}
				variant="text"
				sx={{ backgroundColor: 'white', color: 'black' }}
				startIcon={<Icon path={mdiArrowLeft} size={1} />}
			>
				{t('back')}
			</Button>

			{/* Status Filter */}
			<FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
				<InputLabel id="status-select-label">{t('status')}</InputLabel>
				<Select
					labelId="status-select-label"
					id="status-select"
					value={statusFilter}
					onChange={handleStatusFilterChange}
					label={t('status')}
					disabled={isRequestsLoading || apiCallInProgressRef.current}
					startAdornment={isRequestsLoading && <CircularProgress size={16} sx={{ mr: 1 }} />}
				>
					<MenuItem value="ongoing">{t('ongoing', 'Ongoing')}</MenuItem>
					<MenuItem value="completed">{t('completed', 'Completed')}</MenuItem>
					<MenuItem value="archived">{t('archived', 'Archived')}</MenuItem>
				</Select>
			</FormControl>
		</Box>
	);

	return (
		<Container maxWidth="xl" sx={{ py: 4 }}>
			{headerSection}

			{/* Show loader when loading */}
			{isContentLoading && <SectionLoader />}

			{/* Show content when not loading */}
			{!isContentLoading && (
				<>
					{isEmpty ? (
						<Box
							sx={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								minHeight: '50vh',
							}}
						>
							<EmptyPage
								title={
									statusFilter === 'completed'
										? t('noCompletedOffersYet')
										: statusFilter === 'archived'
											? t('noArchivedOffersYet')
											: t('noRequests')
								}
								buttonText={
									statusFilter === 'completed' || statusFilter === 'archived'
										? t('viewCurrent')
										: t('goToServices')
								}
								onButtonClick={() => {
									statusFilter === 'completed' || statusFilter === 'archived'
										? setStatusFilter('ongoing')
										: navigateWithLanguage('/farmer/services');
								}}
							/>
						</Box>
					) : (
						<>
							{isMobile ? (
								// Mobile layout: Single column with collapsible offers
								<Box
									sx={{
										p: 2,
										bgcolor: 'background.paper',
										borderRadius: '8px',
										boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.05)',
									}}
								>
									<RequestsList
										requests={requests}
										selectedRequestId={selectedRequestId}
										isLoading={false}
										error={requestsError}
										onSelectRequest={handleSelectRequest}
										offers={offers}
										isOffersLoading={false}
										offersError={offersError}
										isMobile={isMobile}
										requestStatus={getExpressStatus(statusFilter)}
										onRefreshOffers={refreshOffers}
									/>
								</Box>
							) : (
								// Desktop layout: Two columns side by side
								<Grid container spacing={3}>
									{/* Requests Panel (Left Side) */}
									<Grid item xs={12} md={6} lg={6}>
										<Box
											sx={{
												p: 2,
												bgcolor: 'background.paper',
												borderRadius: '8px',
												boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.05)',
												height: '100%',
												minHeight: '70vh',
												maxHeight: '80vh',
												overflow: 'auto',
											}}
										>
											<Typography variant="h5" sx={{ mb: 3, fontWeight: 500 }}>
												{t('requests', 'Requests')}
											</Typography>

											<RequestsList
												requests={requests}
												selectedRequestId={selectedRequestId}
												isLoading={false}
												error={requestsError}
												onSelectRequest={handleSelectRequest}
												offers={offers}
												isOffersLoading={false}
												offersError={offersError}
												isMobile={isMobile}
												requestStatus={getExpressStatus(statusFilter)}
												onRefreshOffers={refreshOffers}
											/>
										</Box>
									</Grid>

									{/* Offers Panel (Right Side) */}
									<Grid item xs={12} md={6} lg={6}>
										<Box
											sx={{
												p: 2,
												bgcolor: 'background.paper',
												borderRadius: '8px',
												boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.05)',
												height: '100%',
												minHeight: '70vh',
												maxHeight: '80vh',
												overflow: 'auto',
											}}
										>
											<OffersList
												offers={offers}
												isLoading={false}
												error={offersError}
												requestId={selectedRequestId || ''}
												userId={
													requests.find((r) => (r.request_id || r.id) === selectedRequestId)
														?.specialist_id || ''
												}
												requestStatus={getExpressStatus(statusFilter)}
												onRefreshOffers={refreshAll}
											/>
										</Box>
									</Grid>
								</Grid>
							)}
						</>
					)}
				</>
			)}
		</Container>
	);
};
